/**
 * useCritiqueModeKeyboard - Keyboard shortcuts for critique mode
 * Feature: Refactoring CollectionCritiqueModePage
 *
 * Consolidates all keyboard shortcut registrations for:
 * - Photo navigation (next/prev)
 * - View modes (dimmed lights, fullscreen, exit)
 * - Zoom controls
 * - Help modal
 */

import { useCallback } from 'react';
import { useCommandHandler } from '@/utils/commands';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import type { ZoomLevel } from './useZoomPan';
import type { ViewMode } from '@/contexts/ViewModeContext';
import type { PhotoItem2ViewModel } from '@/components/PhotoItem2/PhotoItem2.types';

export interface CritiqueModeKeyboardParams {
  // Photo data
  photos: PhotoItem2ViewModel[];

  // URL state
  urlState: {
    photoId: string | null;
  };

  // View mode
  viewMode: ViewMode;

  // Zoom level
  sessionZoomLevel: ZoomLevel;

  // Navigation functions
  selectPhoto: (photoId: string) => void;

  // State setters
  setShowKeyboardShortcuts: (show: boolean | ((prev: boolean) => boolean)) => void;
  setSessionZoomLevel: (level: ZoomLevel) => void;

  // Handlers
  toggleDimmedLights: () => void;
  toggleFullscreen: () => void;
  exitViewMode: () => void;
}

export function useCritiqueModeKeyboard(params: CritiqueModeKeyboardParams) {
  const {
    photos,
    urlState,
    viewMode,
    sessionZoomLevel,
    selectPhoto,
    setShowKeyboardShortcuts,
    setSessionZoomLevel,
    toggleDimmedLights,
    toggleFullscreen,
    exitViewMode,
  } = params;

  // Handle keyboard navigation shortcuts using command system
  // Disabled in fullscreen mode to prevent accidental photo switching
  useCommandHandler(
    'critique.nextPhoto',
    useCallback(() => {
      if (photos.length > 0 && urlState.photoId) {
        const currentIndex = photos.findIndex((p) => p.photoData.id === urlState.photoId);
        if (currentIndex >= 0 && currentIndex < photos.length - 1) {
          selectPhoto(photos[currentIndex + 1].photoData.id);
        }
      }
    }, [photos, urlState.photoId, selectPhoto]),
    photos.length > 0 && viewMode !== 'fullscreen'
  );

  useCommandHandler(
    'critique.prevPhoto',
    useCallback(() => {
      if (photos.length > 0 && urlState.photoId) {
        const currentIndex = photos.findIndex((p) => p.photoData.id === urlState.photoId);
        if (currentIndex > 0) {
          selectPhoto(photos[currentIndex - 1].photoData.id);
        }
      }
    }, [photos, urlState.photoId, selectPhoto]),
    photos.length > 0 && viewMode !== 'fullscreen'
  );

  // Keyboard shortcuts help modal handler (Feature 057-lightbox-fullscreen-oneup)
  useCommandHandler(
    'help.showKeyboardShortcuts',
    useCallback(() => {
      setShowKeyboardShortcuts((prev) => !prev); // Toggle modal
    }, [setShowKeyboardShortcuts]),
    true // Always enabled
  );

  // Register view mode keyboard handlers (Feature 057-lightbox-fullscreen-oneup)
  useCommandHandler(
    'viewMode.toggleDimmedLights',
    toggleDimmedLights,
    true // Always enabled
  );

  useCommandHandler(
    'viewMode.toggleFullscreen',
    toggleFullscreen,
    true // Always enabled
  );

  useCommandHandler(
    'viewMode.exitMode',
    exitViewMode,
    viewMode !== 'normal' // Only active when not in normal mode
  );

  // Keyboard shortcuts for zoom control (Feature 646-critique-zoom-control)
  useKeyboardShortcuts({
    currentZoom: sessionZoomLevel,
    onZoomChange: setSessionZoomLevel,
    enabled: true,
  });
}
